/**
 * ATS-style image lazy loading for Dawn theme
 * Implements blur-up technique with intersection observer
 */

class ATSImageLoader {
  constructor() {
    this.images = new Set();
    this.imageObserver = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupObserver());
    } else {
      this.setupObserver();
    }
  }

  setupObserver() {
    // Create intersection observer for better performance
    this.imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      // Start loading when image is 100px away from viewport (increased from 50px)
      rootMargin: '100px 0px',
      threshold: 0.01
    });

    // Find all lazy load images and observe them
    this.observeImages();
  }

  observeImages() {
    const lazyImages = document.querySelectorAll('.ats-main-image.lazyload');
    
    lazyImages.forEach(img => {
      // Skip if already processed
      if (this.images.has(img)) return;
      
      this.images.add(img);
      this.imageObserver.observe(img);
    });
  }

  loadImage(img) {
    return new Promise((resolve, reject) => {
      const container = img.closest('.ats-image-container');
      
      // Check if data-src exists
      if (!img.dataset.src) {
        console.warn('ATS: No data-src found for image', img);
        reject(new Error('No data-src attribute'));
        return;
      }
      
      // Check if already loading or loaded
      if (img.classList.contains('lazyloaded') || img.classList.contains('loading')) {
        resolve(img);
        return;
      }
      
      img.classList.add('loading');
      
      // For critical images, skip preloading and update directly
      if (img.dataset.priority === 'high') {
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
        }
        img.src = img.dataset.src;
        
        img.onload = () => {
          img.classList.remove('lazyload', 'loading');
          img.classList.add('lazyloaded');
          if (container) {
            container.classList.add('loaded');
          }
          resolve(img);
        };
        
        img.onerror = () => {
          img.classList.add('lazyerror');
          if (container) container.classList.add('error');
          reject(new Error('Critical image failed to load'));
        };
        
        return;
      }
      
      // Create a new image to preload (for non-critical images)
      const newImg = new Image();
      
      // Set up success handler
      newImg.onload = () => {
        // Update the actual img element
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
        }
        img.src = img.dataset.src;
        
        // Remove lazyload class and add loaded class to container
        img.classList.remove('lazyload', 'loading');
        img.classList.add('lazyloaded');
        
        // Add loaded class with slight delay for premium transition feel
        setTimeout(() => {
          if (container) {
            container.classList.add('loaded');
          }
        }, 50);
        
        resolve(img);
      };
      
      // Set up error handler
      newImg.onerror = () => {
        console.error('ATS: Failed to load image:', img.dataset.src);
        img.classList.remove('lazyload', 'loading');
        img.classList.add('lazyerror');
        
        // Show fallback
        if (container) {
          container.classList.add('error');
        }
        
        // Try to fallback to original src if available
        if (img.src && img.src !== img.dataset.src) {
          console.log('ATS: Falling back to original src');
          img.style.opacity = '1';
          resolve(img);
        } else {
          reject(new Error('Image failed to load: ' + img.dataset.src));
        }
      };
      
      // Start loading
      if (img.dataset.srcset) {
        newImg.srcset = img.dataset.srcset;
      }
      newImg.src = img.dataset.src;
    });
  }

  // Method to manually observe new images (for dynamic content)
  observeNewImages() {
    this.observeImages();
  }

  // Preload critical images (above the fold)
  preloadCriticalImages() {
    const criticalImages = document.querySelectorAll('.ats-main-image[data-priority="high"], .ats-main-image.lazyload');
    
    // Load first 2 images immediately
    criticalImages.forEach((img, index) => {
      if (index < 2) {
        img.dataset.priority = 'high';
        this.loadImage(img);
      }
    });
  }
}

// Enhanced image loading with additional features
class EnhancedATSLoader extends ATSImageLoader {
  constructor() {
    super();
    this.setupHoverPreloading();
    this.setupProgressiveEnhancement();
  }

  setupHoverPreloading() {
    // Preload images on hover for faster subsequent loads
    document.addEventListener('mouseover', (e) => {
      const productCard = e.target.closest('.card-wrapper, .product-card');
      if (!productCard) return;

      const lazyImage = productCard.querySelector('.ats-main-image.lazyload');
      if (lazyImage && this.images.has(lazyImage)) {
        // Preload on hover with lower priority
        this.loadImage(lazyImage).catch(() => {
          // Ignore errors for hover preloading
        });
      }
    });
  }

  setupProgressiveEnhancement() {
    // Add support for connection-aware loading
    if ('connection' in navigator) {
      const connection = navigator.connection;
      
      if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
        // For slow connections, increase intersection margin
        this.imageObserver.disconnect();
        this.imageObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.loadImage(entry.target);
              observer.unobserve(entry.target);
            }
          });
        }, {
          rootMargin: '20px 0px', // Smaller margin for slow connections
          threshold: 0.1
        });
        
        this.observeImages();
      }
    }

    // Handle reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.documentElement.style.setProperty('--transition-duration', '0s');
    }
  }
}

// Initialize the loader
let atsLoader;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeATSLoader);
} else {
  initializeATSLoader();
}

function initializeATSLoader() {
  atsLoader = new EnhancedATSLoader();
  
  // Preload critical images immediately
  atsLoader.preloadCriticalImages();
}

// Re-observe images when new content is loaded (for AJAX)
document.addEventListener('shopify:section:load', () => {
  if (atsLoader) {
    setTimeout(() => atsLoader.observeNewImages(), 100);
  }
});

// Handle theme editor changes
document.addEventListener('shopify:section:reorder', () => {
  if (atsLoader) {
    setTimeout(() => atsLoader.observeNewImages(), 100);
  }
});

// Export for use in other scripts
window.ATSImageLoader = EnhancedATSLoader;